// Import projects from the tenant's own Ready (BOSS) connection. Lists their Ready
// ReportingProject applications, lets them pick, and creates linked FF projects.
// If Ready isn't connected yet, points them at Integrations to connect their key.
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

type Row = {
  ready_id: string; name: string; status: string | null;
  bsl_count: number | null; bsl_speeds: string | null;
  subgrantee_match_usd: number | null; fixed_amount_subaward: string | null;
  already_imported: boolean;
};

export default function ReadyImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [state, setState] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await api.get('/ready/import/projects');
        setConnected(!!d.connected);
        setRows(Array.isArray(d.items) ? d.items : []);
        if (!d.connected) setErr('Ready is not connected yet.');
      } catch (e: any) { setErr(e?.message ?? 'Could not reach Ready'); }
      finally { setLoading(false); }
    })();
  }, []);

  const pickable = rows.filter((r) => !r.already_imported);
  const chosen = pickable.filter((r) => sel[r.ready_id]).map((r) => r.ready_id);
  const allOn = pickable.length > 0 && chosen.length === pickable.length;

  const doImport = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post('/ready/import/projects', { ready_ids: chosen, state: state.trim().toUpperCase() || undefined });
      setResult(r);
      if ((r?.inserted ?? 0) > 0) onDone();
    } catch (e: any) { setErr(e?.message ?? 'Import failed'); }
    finally { setBusy(false); }
  };

  const money = (n: number | null) => (n == null ? '—' : '$' + Number(n).toLocaleString());

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '6vh 16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: 780, maxWidth: '100%', maxHeight: '86vh', overflow: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e2e8f0' }}>
          <b style={{ fontSize: 16 }}>Import projects from Ready</b>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        <div style={{ padding: 18 }}>
          {loading && <div style={{ color: '#64748b', fontSize: 13 }}>Loading your Ready projects…</div>}

          {!loading && !connected && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: 12, borderRadius: 6, fontSize: 13 }}>
              Ready isn’t connected on this instance yet. Go to <b>Settings → Integrations → Ready</b> and connect your
              account, then come back — your projects will be listed here to import.
            </div>
          )}

          {!loading && connected && (
            <>
              <div style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
                Found <b>{rows.length}</b> project{rows.length === 1 ? '' : 's'} in Ready. Pick the ones to bring in — each becomes a
                project here, linked back to its Ready application so passings and draws report against it.
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <label style={{ fontSize: 12, color: '#475569' }}>
                  State (for the award these attach to){' '}
                  <input value={state} onChange={(e) => setState(e.target.value)} placeholder="AR" maxLength={2}
                    style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 4, width: 60, textTransform: 'uppercase' }} />
                </label>
                {pickable.length > 0 && (
                  <button onClick={() => { const n: Record<string, boolean> = {}; if (!allOn) pickable.forEach((r) => (n[r.ready_id] = true)); setSel(n); }}
                    style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
                    {allOn ? 'Clear all' : `Select all ${pickable.length}`}
                  </button>
                )}
              </div>

              {!result && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'auto', maxHeight: 320, marginBottom: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead><tr style={{ background: '#f8fafc' }}>
                      <th style={{ width: 30 }}></th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Project</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>BSLs</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>Match</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Ready status</th>
                    </tr></thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.ready_id} style={{ borderTop: '1px solid #f1f5f9', opacity: r.already_imported ? 0.5 : 1 }}>
                          <td style={{ padding: '5px 8px' }}>
                            <input type="checkbox" disabled={r.already_imported} checked={!!sel[r.ready_id]}
                              onChange={(e) => setSel((s) => ({ ...s, [r.ready_id]: e.target.checked }))} />
                          </td>
                          <td style={{ padding: '5px 8px', fontWeight: 500 }}>{r.name}
                            {r.already_imported && <span style={{ marginLeft: 6, fontSize: 10, color: '#64748b' }}>already imported</span>}
                          </td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.bsl_count ?? '—'}</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(r.subgrantee_match_usd)}</td>
                          <td style={{ padding: '5px 8px', color: '#64748b' }}>{r.status ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {result && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: 12, fontSize: 13, marginBottom: 12 }}>
              <b>Imported {result.inserted}</b>{result.skipped ? ` · skipped ${result.skipped} already here` : ''}.
            </div>
          )}
          {err && connected && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 10px', borderRadius: 4, fontSize: 13, marginBottom: 10 }}>{err}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '7px 14px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>{result ? 'Close' : 'Cancel'}</button>
            {connected && !result && (
              <button onClick={doImport} disabled={busy || chosen.length === 0}
                style={{ padding: '7px 14px', border: 'none', borderRadius: 4, background: chosen.length ? '#2563eb' : '#94a3b8', color: '#fff', cursor: chosen.length ? 'pointer' : 'default' }}>
                {busy ? 'Importing…' : `Import ${chosen.length || ''} project${chosen.length === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
